/* eslint-disable no-restricted-globals, no-restricted-syntax -- Native tools use the modern PWA browser baseline. */
/** Touch-first native battle tools. */
import preact from '../js/lib/preact';
import { type Battle, type Pokemon } from './battle';
import { Dex, toID } from './battle-dex';
import { PSIcon } from './panels';
import { BattleTools, type BattleToolCombatant, type BattleToolAssumptions } from './battle-tools';
import { BattleToolsData } from './battle-tools-data';

export function BattleTeraTracker({ battle }: { battle: Battle }) {
	if (battle.gen !== 9) return null;
	const sides = [battle.nearSide, battle.farSide].concat(
		battle.sides.filter(side => side !== battle.nearSide && side !== battle.farSide)
	);
	return <div class="battle-tera-tracker" aria-label="Terastallization tracker">
		{sides.map(side => {
			const seen = side.pokemon.find(pokemon => pokemon.terastallized);
			const used = side.terastallization || (seen ? {
				species: seen.speciesForme, type: seen.terastallized, turn: 0,
			} : null);
			return <div class={`battle-tera-record${used ? ' used' : ''}`} key={side.sideid}>
				<span class="battle-tera-player">
					{battle.myPokemon?.[0]?.ident.startsWith(`${side.sideid}:`) ? 'You' : side.name || side.sideid}
				</span>
				{used ? <span title={used.turn ? `Terastallized on turn ${used.turn}` : 'Terastallized'}>
					<strong>{used.species}</strong> <PSIcon type={used.type} new tera />
				</span> : <span class="battle-tera-unused">◇ Tera not used</span>}
			</div>;
		})}
	</div>;
}

export class BattleToolsPanel extends preact.Component<{ battle: Battle }> {
	selected = ['', ''];
	assumptions: { [key: string]: BattleToolAssumptions } = {};
	direction = 0;
	critical = false;
	manualMove = '';
	loading = true;
	error = '';
	alive = false;
	override componentDidMount() { this.alive = true; this.load(); }
	override componentWillUnmount() { this.alive = false; }
	override componentDidUpdate() {
		if (!this.loading && Date.now() - BattleToolsData.lastAttempt >= 3600000) this.refresh();
	}
	load = async () => {
		this.loading = true;
		this.error = '';
		this.forceUpdate();
		try {
			await Promise.all([BattleTools.loadEngine(), BattleToolsData.load(), BattleTools.checkEngineVersion()]);
		} catch {
			this.error = 'The calculator could not load. Check your connection and retry.';
		} finally {
			this.loading = false;
			if (this.alive) this.forceUpdate();
		}
	};
	refresh = async () => {
		this.loading = true;
		this.forceUpdate();
		await Promise.all([BattleToolsData.load(true), BattleTools.checkEngineVersion()]);
		this.loading = false;
		if (this.alive) this.forceUpdate();
	};
	key(pokemon: Pokemon) { return `${pokemon.side.sideid}:${pokemon.ident}:${pokemon.speciesForme}`; }
	change(subject: BattleToolCombatant, key: 'role' | 'ability' | 'item' | 'tera', value: string) {
		const id = this.key(subject.pokemon);
		const options = (this.assumptions[id] ||= {});
		options[key] = value;
		if (key === 'role') {
			delete options.item;
			delete options.ability;
			delete options.stats;
		}
		this.forceUpdate();
	}
	renderSubject(subject: BattleToolCombatant, index: number, team: Pokemon[]) {
		const { battle } = this.props;
		const { pokemon, server, role, set, assumptions } = subject;
		const preview = !pokemon.terastallized && !!assumptions.tera;
		const stats = BattleTools.createPokemon(battle, subject).rawStats;
		const roleNames = Object.keys(set?.roles || {});
		const types = ['Normal', 'Fire', 'Water', 'Electric', 'Grass', 'Ice', 'Fighting', 'Poison', 'Ground',
			'Flying', 'Psychic', 'Bug', 'Rock', 'Ghost', 'Dragon', 'Dark', 'Steel', 'Fairy'];
		const pool = (values: string[], selected: string) => Array.from(new Set([selected, ...values])).filter(Boolean);
		const abilities = pool(role?.abilities || Object.values(pokemon.getSpecies().abilities), subject.ability);
		const items = pool(role?.items || [], subject.item);
		return <section class={`battle-tool-subject${preview ? ' previewing' : ''}`}>
			<label class="battle-tool-picker">
				<span>{index === 0 ? 'Your side' : 'Opponent'} <small>{server ? 'Known stats' : 'Estimated stats'}</small></span>
				<select
					aria-label={index === 0 ? 'Your Pokémon' : 'Opponent Pokémon'} value={this.selected[index]}
					onChange={event => { this.selected[index] = (event.currentTarget as HTMLInputElement).value; this.forceUpdate(); }}
				>
					<option value="">Follow active Pokémon</option>
					{team.map(member => <option value={this.key(member)} key={this.key(member)}>
						{member.speciesForme}{member.fainted ? ' (fainted)' : ''}
					</option>)}
				</select>
			</label>
			<div class="battle-tool-identity">
				<span style={Dex.getPokemonIcon(pokemon.speciesForme)} class="picon" />
				<strong>{pokemon.speciesForme}</strong><small>L{pokemon.level} · {pokemon.getHPText()}</small>
			</div>
			{!server && roleNames.length > 0 && <label class="battle-tool-label">Assumed set{}
				<select
					aria-label={`Assumed set for ${pokemon.speciesForme}`} value={subject.roleName}
					onChange={event => this.change(subject, 'role', (event.currentTarget as HTMLInputElement).value)}
				>
					{roleNames.map(name => <option value={name} key={name}>
						{name}{subject.compatibleRoles.includes(name) ? '' : ' (does not match revealed moves)'}
					</option>)}
				</select>
			</label>}
			<div class="battle-tool-attributes">
				<label>Ability {!subject.abilityKnown && <small>assumed</small>}
					<select
						aria-label={`Ability for ${pokemon.speciesForme}`} value={subject.ability}
						disabled={subject.abilityKnown}
						onChange={event => this.change(subject, 'ability', (event.currentTarget as HTMLInputElement).value)}
					>
						<option value="">{subject.abilityKnown ? 'Suppressed / none' : 'Unknown / no modifier'}</option>
						{abilities.map(name => <option value={name} key={name}>{name}</option>)}
					</select>
				</label>
				<label>Item {!subject.itemKnown && <small>assumed</small>}
					{subject.itemKnown ? <span class="battle-tool-known">{subject.item || 'None'}</span> : <input
						aria-label={`Item for ${pokemon.speciesForme}`} list={`tool-items-${pokemon.side.sideid}`}
						value={subject.item} placeholder="Unknown / none"
						onChange={event => this.change(subject, 'item', (event.currentTarget as HTMLInputElement).value.trim())}
					/>}
					<datalist id={`tool-items-${pokemon.side.sideid}`}>{items.map(name => <option value={name} key={name} />)}</datalist>
				</label>
			</div>
			<label class="battle-tool-tera">{pokemon.terastallized ? 'Terastallized' : 'What if Tera?'}
				<select
					aria-label={`Tera preview for ${pokemon.speciesForme}`} value={pokemon.terastallized || assumptions.tera || ''}
					disabled={!!pokemon.terastallized || !!pokemon.side.terastallization}
					onChange={event => this.change(subject, 'tera', (event.currentTarget as HTMLInputElement).value)}
				>
					<option value="">Current state</option>
					{types.map(type => <option value={type} key={type}>
						{type}{(server?.teraType === type || role?.teraTypes.includes(type)) ? ' ★' : ''}
					</option>)}
					{pokemon.terastallized === 'Stellar' && <option value="Stellar">Stellar</option>}
				</select>
			</label>
			<details class="battle-tool-details">
				<summary>Stats & revealed moves</summary>
				<p>{server ? 'Exact unboosted stats from your battle request.' :
				subject.random ? 'Neutral nature, 84 EVs / 31 IVs per stat, with known randbats exceptions. Edit estimates below.' :
				'Neutral nature, 0 EVs / 31 IVs assumed. Edit stats for the set you expect.'}</p>
				<div class="battle-tool-stats">{Dex.statNames.map(stat => <label key={stat}>
					{stat.toUpperCase()} <input
						type="number" min="1" max="9999" inputMode="numeric"
						aria-label={`${pokemon.speciesForme} ${stat.toUpperCase()}`} value={stats[stat]} disabled={!!server}
						onChange={event => {
							const value = Number((event.currentTarget as HTMLInputElement).value);
							if (!Number.isInteger(value) || value < 1 || value > 9999) return;
							const options = (this.assumptions[this.key(pokemon)] ||= {});
							(options.stats ||= {})[stat] = value;
							this.forceUpdate();
						}}
					/>
				</label>)}</div>
				<p><strong>Revealed:</strong> {subject.revealed.join(', ') || 'No moves yet'}</p>
				{role && <p><strong>Possible moves:</strong> {role.moves.join(', ')}</p>}
				{role && <p><strong>Possible items:</strong> {role.items.join(', ') || 'Unknown'}</p>}
				{role && <p><strong>Possible Tera types:</strong> {role.teraTypes.join(', ')}</p>}
			</details>
		</section>;
	}
	override render() {
		const { battle } = this.props;
		if (this.error) return <div class="battle-tools"><p role="status">{this.error}</p>
			<button class="button" onClick={() => void this.load()}>Retry calculator</button></div>;
		if (!BattleTools.ready) return <div class="battle-tools"><p role="status">Loading damage engine…</p></div>;
		if (battle.gen !== 9 || battle.gameType !== 'singles' || battle.hardcoreMode) return <div class="battle-tools">
			<p>{battle.hardcoreMode ? 'Damage tools are hidden in hardcore mode.' :
			'Damage tools currently support Gen 9 singles. Tera tracking is available in every Gen 9 battle.'}</p>
		</div>;
		const teams = [BattleTools.team(battle, battle.nearSide), BattleTools.team(battle, battle.farSide)];
		const subjects = teams.map((team, index) => {
			const active = [battle.nearSide, battle.farSide][index].active[0];
			const pokemon = team.find(member => this.key(member) === this.selected[index]) || active || team[0];
			return pokemon ? BattleTools.combatant(battle, pokemon, this.assumptions[this.key(pokemon)]) : null;
		});
		if (!subjects[0] || !subjects[1]) return <div class="battle-tools">
			<p>Waiting for both Pokémon to be revealed.</p></div>;
		const attacker = subjects[this.direction]!;
		const defender = subjects[1 - this.direction]!;
		const unsupported = BattleTools.unsupported(battle, attacker, defender);
		let cards;
		try { cards = subjects.map((subject, index) => this.renderSubject(subject!, index, teams[index])); } catch {
			return <div class="battle-tools"><p>This Pokémon is not supported by the damage engine yet.</p></div>;
		}
		const preview = subjects.some(subject => !!subject!.assumptions.tera && !subject!.pokemon.terastallized);
		const weather = battle.weather ? Dex.getEffect(battle.weather).name : '';
		const terrain = ['Electric', 'Grassy', 'Psychic', 'Misty'].find(type => battle.hasPseudoWeather(`${type} Terrain`));
		return <div class="battle-tools">
			<div class="battle-tools-heading"><div><span class="battle-tools-eyebrow">BATTLE TOOLS</span>
				<h3>Damage calculator</h3></div>
			<button
				class="button" onClick={() => {
					this.assumptions = {}; this.critical = false; this.forceUpdate();
				}}
			>Reset</button>
			</div>
			<p class="battle-tools-note">{preview ? 'Tera preview — your battle choice is unchanged. ' : ''}
				Opponent sets are estimates. Edit assumptions to explore Tera.</p>
			{BattleTools.engineStatus.startsWith('Damage engine update') && <p class="battle-tools-note" role="status">
				{BattleTools.engineStatus}. Calculations use {BattleTools.engineVersion} until the client is updated.
			</p>}
			<details class="battle-tool-edit">
				<summary>Edit matchup & assumptions <span>{preview ? 'Tera preview on' : 'Sets · items · Tera'}</span></summary>
				<div class="battle-tool-subjects">{cards}</div>
				<button
					class="button battle-tool-reset" onClick={() => {
						this.assumptions = {}; this.critical = false; this.forceUpdate();
					}}
				>Reset assumptions</button>
			</details>
			<div class="battle-tool-direction" role="group" aria-label="Damage direction">
				{['Deal damage', 'Take damage'].map((label, index) => <button
					key={label}
					aria-pressed={this.direction === index} onClick={() => { this.direction = index; this.forceUpdate(); }}
				>
					{label}
				</button>)}
			</div>
			<div class="battle-tool-results" aria-label="Damage estimates">
				{unsupported ? <p>{unsupported}</p> : !battle.atQueueEnd && battle.myPokemon ?
					<p>Turn playing — calculations resume when the battle catches up.</p> : <>
						<p class="battle-tool-target">{attacker.pokemon.speciesForme} → {defender.pokemon.speciesForme}
							<small>Damage as % of maximum HP</small></p>
						<p class="battle-tool-assumption-summary">Defender: {defender.item || 'no item modifier'} · {}
							{defender.ability || 'no ability modifier'}{defender.server ? ' · known stats' : ' · estimated stats'}</p>
						{(attacker.moves.length || this.manualMove) ? Array.from(new Set(
							attacker.moves.concat(this.manualMove ? [this.manualMove] : [])
						)).map(move => {
							const result = BattleTools.calculate(battle, attacker, defender, move, this.critical);
							const revealed = attacker.server?.moves.some(name => toID(name) === toID(move)) ||
								attacker.revealed.some(name => toID(name) === toID(move));
							return <div class="battle-tool-damage" key={move}>
								<div><strong>{result.move}</strong><span>{result.text}</span></div>
								<small>{result.type} · {}
									{revealed ? 'Known move' : move === this.manualMove ? 'Test move' : 'Possible move'} · {result.detail}
								</small>
								{result.max !== undefined && <div class="battle-tool-damage-bar" aria-hidden>
									<span style={{ width: `${Math.min(100, result.max)}%` }} />
									<strong style={{ width: `${Math.min(100, result.min!)}%` }} />
								</div>}
							</div>;
						}) : <p>No revealed moves yet. Random Battle move pools appear once the set data loads.</p>}
					</>}
			</div>
			<div class="battle-tool-field"><span>
				{weather || 'No weather'}{terrain ? ` · ${terrain} Terrain` : ''} · Turn {battle.turn}
			</span>
			<label><input
				type="checkbox" checked={this.critical}
				onChange={event => { this.critical = (event.currentTarget as HTMLInputElement).checked; this.forceUpdate(); }}
			/> Critical hit</label>
			</div>
			<label class="battle-tool-manual">Test another move{}
				<input
					type="text" placeholder="Move name" aria-label="Test another move" value={this.manualMove}
					onChange={event => {
						const value = (event.currentTarget as HTMLInputElement).value.trim();
						this.manualMove = value ? battle.dex.moves.get(value).name : '';
						this.forceUpdate();
					}}
				/>
			</label>
			<p class="battle-tools-note">Boosts, status, weather, terrain, screens, items and Tera follow the battle.
				Ranges assume the move succeeds; they do not predict move choice, accuracy or a KO.</p>
			<details class="battle-tool-details battle-tool-data">
				<summary>Data & accuracy</summary>
				<p>{BattleToolsData.status}{BattleToolsData.checkedAt && <>
					· Checked {new Date(BattleToolsData.checkedAt).toLocaleString()}</>}</p>
				<p>Smogon damage engine {BattleTools.engineVersion}. Current standard Gen 9 Random Battle pools come from
					Showdown, with sampled item and spread options from pkmn/randbats. Historical replays use current pools.</p>
				{BattleTools.engineStatus && <p>{BattleTools.engineStatus}</p>}
				<p>Hidden sets, rounded HP, move-dependent spreads and conditional moves can change a result.
					Multihit moves show the assumed hit count. Transform and Stellar boost history are not supported.</p>
				<button class="button" onClick={() => void this.refresh()} disabled={this.loading}>
					{this.loading ? 'Checking…' : 'Refresh set data'}
				</button>
				<p><a href="https://github.com/smogon/damage-calc" target="_blank" rel="noreferrer">Damage engine</a> · {}
					<a href="https://github.com/pkmn/randbats" target="_blank" rel="noreferrer">Set data</a> · {}
					<a href="/battle-data/LICENSE.txt" target="_blank" rel="noreferrer">Licenses</a></p>
			</details>
		</div>;
	}
}
